'use client';

import { useMemo } from 'react';
import {
  ADDITIONAL_MAX_FEATURES_V2,
  ADDITIONAL_PRO_FEATURES_V2,
  ADDITIONAL_STANDARD_FEATURES_V2,
  ADDITIONAL_ULTRA_FEATURES_V2,
  BUSINESS_FEATURES_V2,
  getNewMaxUsageFeatures,
  INDIVIDUAL_FEATURES_V2,
  MAX_FEATURES_V2,
  NEW_MAX_USAGE_DATASET,
  PRO_FEATURES_V2,
  STANDARD_FEATURES_V2,
  ULTRA_FEATURES_V2,
} from 'src/constants';
import styles from './PlanCardContent.module.scss';
import { PlanCardContentItem } from '../PlanCardContentItem';
import { RequestUsageTrigger } from '../../components/RequestUsage/components/RequestUsageTrigger';
import { CanonicalInterval, PlanFamily, PlanKind } from 'src/types';
import { UserTier } from 'src/types/wallet/models/UserTier';
import { useEntitlements } from 'src/hooks';

interface PlanCardContentProps {
  planFamily: PlanFamily;
  planKind: PlanKind;
  planTier?: UserTier;
}

export const PlanCardContent = ({
  planFamily,
  planKind,
  planTier,
}: PlanCardContentProps) => {
  const isBusiness = planFamily === PlanFamily.BUSINESS;

  const { entitlements } = useEntitlements({
    isBusiness,
    interval: planKind.includes('monthly')
      ? CanonicalInterval.MONTHLY
      : CanonicalInterval.ANNUALLY,
  });

  const usageFeatures = useMemo(() => {
    const features = NEW_MAX_USAGE_DATASET[planKind];
    const numberOfRequests =
      entitlements.find((item) => item.tier === planTier)?.limits.total.value ||
      0;

    return !!features
      ? getNewMaxUsageFeatures({
          ...features,
          requestCount: numberOfRequests.toString().replace('-1', 'Unlimited'),
          RequestUsageTriggerComponent: (
            <RequestUsageTrigger isBusiness={isBusiness} />
          ),
        })
      : undefined;
  }, [planKind, isBusiness, entitlements, planTier]);

  const commonFeatures = useMemo(() => {
    if (
      planKind === PlanKind.MAX_05_ANNUALLY ||
      planKind === PlanKind.MAX_05_MONTHLY
    ) {
      return STANDARD_FEATURES_V2;
    }

    if (
      planKind === PlanKind.MAX_10_ANNUALLY ||
      planKind === PlanKind.MAX_10_MONTHLY
    ) {
      return [...PRO_FEATURES_V2, ...INDIVIDUAL_FEATURES_V2];
    }

    if (
      planKind === PlanKind.BUSINESS_10_ANNUALLY ||
      planKind === PlanKind.BUSINESS_10_MONTHLY
    ) {
      return [...PRO_FEATURES_V2, ...BUSINESS_FEATURES_V2];
    }

    if (
      planKind === PlanKind.MAX_30_ANNUALLY ||
      planKind === PlanKind.MAX_30_MONTHLY
    ) {
      return [...MAX_FEATURES_V2, ...INDIVIDUAL_FEATURES_V2];
    }

    if (
      planKind === PlanKind.BUSINESS_30_ANNUALLY ||
      planKind === PlanKind.BUSINESS_30_MONTHLY
    ) {
      return [...MAX_FEATURES_V2, ...BUSINESS_FEATURES_V2];
    }

    if (
      planKind === PlanKind.MAX_20_ANNUALLY ||
      planKind === PlanKind.MAX_20_MONTHLY
    ) {
      return [...ULTRA_FEATURES_V2, ...INDIVIDUAL_FEATURES_V2];
    }

    if (
      planKind === PlanKind.BUSINESS_20_MONTHLY ||
      planKind === PlanKind.BUSINESS_20_ANNUALLY
    ) {
      return [...ULTRA_FEATURES_V2, ...BUSINESS_FEATURES_V2];
    }

    return [];
  }, [planKind]);

  const features = useMemo(() => {
    return [...(usageFeatures || []), ...commonFeatures];
  }, [commonFeatures, usageFeatures]);

  const additionalFeatures = useMemo(() => {
    if (
      planKind === PlanKind.MAX_05_ANNUALLY ||
      planKind === PlanKind.MAX_05_MONTHLY
    ) {
      return ADDITIONAL_STANDARD_FEATURES_V2;
    }

    if (
      planKind === PlanKind.MAX_10_ANNUALLY ||
      planKind === PlanKind.MAX_10_MONTHLY ||
      planKind === PlanKind.BUSINESS_10_ANNUALLY ||
      planKind === PlanKind.BUSINESS_10_MONTHLY
    ) {
      return ADDITIONAL_PRO_FEATURES_V2;
    }

    if (
      planKind === PlanKind.MAX_20_ANNUALLY ||
      planKind === PlanKind.MAX_20_MONTHLY ||
      planKind === PlanKind.BUSINESS_20_ANNUALLY ||
      planKind === PlanKind.BUSINESS_20_MONTHLY
    ) {
      return ADDITIONAL_ULTRA_FEATURES_V2;
    }

    if (
      planKind === PlanKind.MAX_30_ANNUALLY ||
      planKind === PlanKind.MAX_30_MONTHLY ||
      planKind === PlanKind.BUSINESS_30_ANNUALLY ||
      planKind === PlanKind.BUSINESS_30_MONTHLY
    ) {
      return ADDITIONAL_MAX_FEATURES_V2;
    }

    return [];
  }, [planKind]);

  return (
    <div className={styles.root}>
      {features.map((item, index) => (
        <PlanCardContentItem key={index} {...item} />
      ))}

      <div className={styles.additionalFeaturesHeaderWrapper}>
        <h4 className={styles.additionalFeaturesTitle}>
          Unlimited Chat & Image AI Agent
        </h4>
        <span className={styles.additionalFeaturesDescription}>
          Code, chat, brainstorm and search with your preferred AI Model
        </span>
      </div>

      {additionalFeatures.map((item, index) => (
        <PlanCardContentItem key={index} {...item} />
      ))}
    </div>
  );
};
